import { Menu, MenuItems, MenuButton, MenuItem } from '@headlessui/react';
import { DotsThreeVertical, Icon } from '@phosphor-icons/react';
import { cn } from 'src/utils/className';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type SideDropAction = {
  label: string;
  Icon?: Icon;
  show?: boolean;
  disabled?: boolean;
  color?:
    | 'primary'
    | 'secondary'
    | 'error'
    | 'warning'
    | 'success'
    | 'info'
    | 'default';
  onClick: () => void;
};

type SideDropActionsProps = {
  options: SideDropAction[];
  zIndex?: number;
  TriggerIcon?: Icon;
};

const variantClasses = {
  primary: 'text-primary',
  secondary: 'text-neutral-400',
  error: 'text-error',
  warning: 'text-warning',
  success: 'text-success',
  info: 'text-info',
  default: 'text-primary',
};

export const SideDropActions = ({
  options,
  zIndex = 10,
  TriggerIcon,
}: SideDropActionsProps) => {
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const ref = useRef<HTMLButtonElement>(null);

  const updatePosition = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPosition({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
  };

  useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, []);

  if (!options.filter(({ show = true }) => show).length) return <div />;
  return (
    <div className="relative leading-none">
      <Menu>
        {({ open }) => (
          <>
            <MenuButton
              ref={ref}
              className={cn(
                'p-1.5 rounded-md transition-colors text-neutral-700',
                'hover:bg-neutral-100 active:bg-neutral-200',
                open && 'bg-neutral-100',
              )}
              aria-label="Actions"
              onClick={updatePosition}
            >
              {TriggerIcon ? (
                <TriggerIcon className="text-xl" weight="bold" />
              ) : (
                <DotsThreeVertical className="text-xl" weight="bold" />
              )}
            </MenuButton>

            {createPortal(
              <MenuItems
                className="flex flex-col absolute bg-neutral-0 border border-neutral-100 shadow-lg rounded-lg focus:outline-none py-1"
                style={{
                  top: `${position.top}px`,
                  left: `${position.left}px`,
                  transform: 'translate(-100%, 8px)',
                  zIndex,
                }}
              >
                {options
                  .filter(({ show = true }) => show)
                  .map(
                    (
                      { label, Icon, color = 'secondary', onClick, disabled },
                      index,
                    ) => (
                      <MenuItem key={index}>
                        {({ close }) => (
                          <button
                            onClick={() => {
                              if (!disabled) {
                                onClick();
                                close();
                              }
                            }}
                            className={cn(
                              'flex items-center grow px-4 py-2 min-w-max',
                              Icon && 'gap-4',
                              !disabled && 'hover:bg-neutral-50',
                              disabled && 'cursor-not-allowed opacity-50',
                              variantClasses[color],
                            )}
                            disabled={disabled}
                          >
                            {Icon && <Icon className="text-xl" />} {label}
                          </button>
                        )}
                      </MenuItem>
                    ),
                  )}
              </MenuItems>,
              document.body,
            )}
          </>
        )}
      </Menu>
    </div>
  );
};
